import { Tooltip } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { API } from '@/api'
import ArrowRight from '@/assets/icons/arrow-right.svg?react'
import { Button, ButtonRadio, Loading, Trans } from '@/components'
import { Pagination } from '@/components/base/Pagination'
import {
  BILL_SOURCE_TYPE_DISPLAY,
  BILL_SUBTYPE_DISPLAY,
  BILL_TYPE_DISPLAY,
  BILL_TYPE_SUBTYPE_DISPLAY,
  isAssetTransferHold,
} from '@/models/bill'
import { getBonusStat } from '@/models/credit'
import { useUserStore } from '@/stores/user'
import { getBillDisplayTimeSeconds } from '@/utils/billingTime'
import { getPersonalTransferTypeStr } from '@/utils/billingTransfer'
import { formatDatePreset, formatDurationToHours, formatPrice } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { BalanceDetailModal } from './components/BalanceDetailModal'
import { ExchargeModal } from './components/RechargeModal'

export function BillingPage() {
  const [searchParams] = useSearchParams()
  const catalogFromUrl = searchParams.get('catalog')
  const initialCatalog = catalogFromUrl ? Number(catalogFromUrl) : 0

  const [loading, setLoading] = useState(false)
  const [catalog, setCatalog] = useState(initialCatalog)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [bills, setBills] = useState<any[]>([])

  // Sync catalog state with URL parameter changes
  useEffect(() => {
    const urlCatalog = searchParams.get('catalog')
    if (urlCatalog !== null) {
      const catalogValue = Number(urlCatalog)
      if (!isNaN(catalogValue)) {
        setCatalog(catalogValue)
        setPage(1)
      }
    }
  }, [searchParams])
  const pageSize = 10
  const userStore = useUserStore()
  const { t } = useTranslation('billing')
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)
  const [balanceDetailOpen, setBalanceDetailOpen] = useState(false)

  const catalogItems = useMemo(() => {
    return [
      {
        label: t('type.all'),
        value: 0,
      },
      {
        label: t('type.instances'),
        value: 1,
      },
      // {
      //   label: 'Bits',
      //   value: -1,
      //   disabled: true,
      // },
      {
        label: t('type.others'),
        value: 2,
      },
    ]
  }, [t])

  const overviewItems = useMemo(() => {
    return [
      {
        key: 'balance',
        label: t('balanceTitle'),
        value: formatPrice(userStore.account.totalBonus.moneyCent ?? 0, false, ''),
        action: () => {
          // fall back to the recharge modal when the backend has no bonusStat yet
          if (getBonusStat(userStore.account)) {
            setBalanceDetailOpen(true)
          } else {
            setExchargeModalOpen(true)
          }
        },
      },
      {
        key: 'total-spending',
        label: t('totalSpending'),
        value: formatPrice(userStore.account.expense.amount ?? 0, false, ''),
      },
      // {
      //   key: 'bit-balance',
      //   label: 'My Bits (Bit)',
      //   value: '-',
      // },
      {
        key: 'total-duration',
        label: t('duration'),
        value: formatDurationToHours(userStore.account.expense.timeSec ?? 0),
      },
      {
        key: 'invoicing-limit',
        label: t('invoicingLimit'),
        value: '-',
        disabled: true,
        action: () => {},
      },
    ]
  }, [userStore, t])

  const fetchBill = async (p = page) => {
    setLoading(true)
    try {
      const res = await API.getUserBills({
        page: p,
        per_page: pageSize,
        catalog: catalog,
      })
      if (res.code === 0) {
        setBills(
          res.bills?.map((b: any) => {
            const { pointsCent } = b.Info?.RechargeInfo || {}

            const rawTypeKey =
              BILL_TYPE_SUBTYPE_DISPLAY[`${b.type}-${b.subType}`] ||
              BILL_SUBTYPE_DISPLAY[b.subType] ||
              BILL_SOURCE_TYPE_DISPLAY[b.sourceType ?? -1] ||
              BILL_TYPE_DISPLAY[b.type] ||
              '-'
            let typeStr = t(`typeStr.${rawTypeKey}`, { defaultValue: rawTypeKey })

            if (rawTypeKey === 'International Site Credits') {
              typeStr =
                getPersonalTransferTypeStr({
                  transferInfo: b.Info?.TransferInfo,
                  billUserId: b.userID,
                  t,
                }) || typeStr
            }

            const paymentStr =
              b.Info !== null && b.Info.RechargeInfo
                ? `${(pointsCent / 100).toFixed(3)} ${Math.abs(Number((pointsCent / 100).toFixed(3))) > 1 ? 'Credits' : 'Credit'}`
                : typeStr === 'Recharge' && b.Info === null
                  ? formatPrice(b.amount, false, Math.abs(b.amount) > 100 ? 'Credits' : 'Credit')
                  : formatPrice(b.amount, false, Math.abs(b.amount) > 100 ? 'Credits' : 'Credit')

            return {
              ...b,
              isUnsettled: b.type === 1,
              isHold: isAssetTransferHold(b),
              typeStr,
              paymentStr,
            }
          }) || [],
        )

        setPage(p)
        setTotal(res.pagination.total)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBill()
  }, [catalog])

  return (
    <div className="flex flex-col h-full">
      <div className="card p-4 overflow-clip bg-panel-white">
        <div className="grid grid-cols-4 text-secondary gap-x-4">
          {overviewItems.map((item) => (
            <div
              key={item.key}
              className={cn(
                'border border-neutral-100 p-6 rounded-2xl',
                item.action ? ' cursor-pointer' : '',
                { 'cursor-not-allowed': item.disabled },
              )}
              onClick={item.action}
            >
              <div className="t-x-sb text-secondary mb-2">{item.label}</div>
              <div className="flex items-center gap-x-4">
                <div className="h5 text-primary flex-grow">{item.value}</div>
                {item.action && <ArrowRight width={20} height={20} />}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card bg-panel-white h-full overflow-y-auto mt-4">
        <div className="flex gap-x-8 h6 items-start justify-between border-b border-neutral-200 pb-4 mb-12">
          <h6 className="text-primary">
            <Trans ns="billing" i18nKey="billingTab">
              My Spending
            </Trans>
          </h6>
          <Button type="secondary" onClick={() => setExchargeModalOpen(true)}>
            <Trans ns="billing" i18nKey="rechargeBtn">
              Recharge
            </Trans>
          </Button>
        </div>

        <div className="mb-6">
          <ButtonRadio
            items={catalogItems}
            value={catalog}
            unselectedClassName={'text-tertiary'}
            onChange={(v) => {
              setPage(1)
              setCatalog(v)
            }}
          />
        </div>

        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <>
            <table className="table mt-4 w-full">
              <thead>
                <tr>
                  <th className="text-left">
                    <Trans i18nKey="profile.billingTableHeaderType">Time</Trans>
                  </th>
                  <th className="text-left" style={{ minWidth: '360px' }}>
                    <Trans i18nKey="profile.billingTableHeaderName">Name</Trans>
                  </th>
                  <th className="text-left">
                    <Trans i18nKey="profile.billingTableHeaderStatus">Status</Trans>
                  </th>

                  <th className="!text-right">
                    <Trans i18nKey="profile.billingTableHeaderPayment">Payment</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {bills.map((data, idx) => (
                  <tr key={idx}>
                    <td>
                      <div className="t-sb text-secondary">
                        {formatDatePreset((getBillDisplayTimeSeconds(data) || 0) * 1000, 'second')}
                      </div>
                    </td>
                    <td className="max-w-0">
                      <Tooltip
                        title={<div className="t-sm break-all">{data.typeStr}</div>}
                        placement="top"
                        mouseEnterDelay={0}
                      >
                        <div className="block w-full overflow-hidden text-ellipsis whitespace-nowrap text-primary t-sb">
                          {data.typeStr}
                        </div>
                      </Tooltip>
                    </td>
                    <td
                      className={cn('t-sb', {
                        'text-highlight': data.isUnsettled,
                        'text-secondary': data.isHold,
                        'text-success': !data.isUnsettled && !data.isHold,
                      })}
                    >
                      {data.isHold ? (
                        <Tooltip title={t('status.holdHint')} placement="top" mouseEnterDelay={0}>
                          <span className="cursor-help underline decoration-dotted">
                            {t('status.hold')}
                          </span>
                        </Tooltip>
                      ) : data.isUnsettled ? (
                        t('status.ongoing')
                      ) : (
                        t('status.settled')
                      )}
                    </td>
                    <td className={cn('text-right t-sb', { 'text-secondary': data.isHold })}>
                      {data.paymentStr}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              size={pageSize}
              total={total}
              current={page}
              className="justify-end mt-4"
              onChange={(page) => fetchBill(page)}
            />
          </>
        )}
      </div>

      {balanceDetailOpen && (
        <BalanceDetailModal
          onClose={() => setBalanceDetailOpen(false)}
          onRecharge={() => {
            setBalanceDetailOpen(false)
            setExchargeModalOpen(true)
          }}
        />
      )}

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            fetchBill()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
